import { useState } from "react";
import { toast } from "sonner";
import { Save, User } from "@/components/icons";
import api, { formatErr } from "../lib/api";
import {
  Input,
  Select,
  Textarea,
  DatePicker,
  sanitizePatient,
} from "../pages/doctor/patient/common";
import AddressAutocomplete from "./AddressAutocomplete";
import AppleModal, {
  AppleModalActions,
  AppleModalPrimaryButton,
  AppleModalSecondaryButton,
} from "./AppleModal";

/**
 * Lightweight patient-info editor for non-clinical roles (Secretary).
 * Same fields as FullInfoTab but WITHOUT exposing clinical history.
 */
export default function EditPatientModal({ patient, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({ ...sanitizePatient(patient) }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.full_name?.trim()) {
      toast.error("Nombre obligatorio");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/patients/${patient.id}`, form);
      toast.success("Paciente actualizado");
      onSaved();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppleModal
      open
      onClose={onClose}
      icon={<User weight="regular" className="w-11 h-11" strokeWidth={1.5} />}
      title="Editar paciente"
      subtitle="Datos personales, contacto y salud básica"
      size="lg"
      testid="edit-patient-modal"
      footer={
        <form onSubmit={save}>
          <AppleModalActions>
            <AppleModalSecondaryButton onClick={onClose} testid="edit-patient-cancel">
              Cancelar
            </AppleModalSecondaryButton>
            <AppleModalPrimaryButton
              type="submit"
              loading={saving}
              onClick={save}
              testid="edit-patient-save"
            >
              <Save className="w-3.5 h-3.5" /> Guardar
            </AppleModalPrimaryButton>
          </AppleModalActions>
        </form>
      }
    >
      <div className="grid sm:grid-cols-2 gap-3">
        <Input label="Nombre completo" value={form.full_name} onChange={(v) => set("full_name", v)} required />
        <Input label="Documento" value={form.document_id} onChange={(v) => set("document_id", v)} />
        <DatePicker label="Fecha de nacimiento" value={form.birth_date} onChange={(v) => set("birth_date", v)} testid="edit-patient-birthdate" />
        <Select
          label="Género"
          value={form.gender}
          onChange={(v) => set("gender", v)}
          options={[
            { v: "", l: "—" },
            { v: "male", l: "Masculino" },
            { v: "female", l: "Femenino" },
            { v: "other", l: "Otro" },
          ]}
        />
        <Input label="Teléfono" value={form.phone} onChange={(v) => set("phone", v)} />
        <Input label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} />
        <Select
          label="Tipo de sangre"
          value={form.blood_type}
          onChange={(v) => set("blood_type", v)}
          options={[
            { v: "", l: "—" },
            ...["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((b) => ({ v: b, l: b })),
          ]}
        />
        <Input label="Alergias" value={form.allergies} onChange={(v) => set("allergies", v)} />
      </div>
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/65">Dirección</span>
        <div className="mt-1.5">
          <AddressAutocomplete
            value={form.address}
            onChange={(v) => set("address", v)}
            showIcon={false}
            inputClassName="w-full px-4 py-2.5 rounded-xl glass-input outline-none"
            testid="edit-patient-address"
          />
        </div>
      </label>
      <Textarea label="Notas" value={form.notes} onChange={(v) => set("notes", v)} rows={2} />
    </AppleModal>
  );
}
